import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Phone, Mail, MapPin, Globe, Download, ArrowLeft, Palette } from 'lucide-react';

// Carte de visite digitale — 3 versions de couleur + QR code
const THEMES = {
  bordeaux: {
    name: 'Bordeaux',
    versoBg: 'bg-gradient-to-br from-[#5C2018] to-[#2E120D]',
    versoTextSwatch: '#5C2018',
    rectoTitle: 'text-[#5C2018]',
    qrFile: 'carte-visite-harmony-bordeaux',
  },
  vert: {
    name: 'Vert émeraude',
    versoBg: 'bg-gradient-to-br from-[#1E7A6A] to-[#0F3F37]',
    versoTextSwatch: '#1E7A6A',
    rectoTitle: 'text-[#1E7A6A]',
    qrFile: 'carte-visite-harmony-vert',
  },
  dore: {
    name: 'Doré',
    versoBg: 'bg-gradient-to-br from-[#B8922A] to-[#7a611c]',
    versoTextSwatch: '#B8922A',
    rectoTitle: 'text-[#5C2018]',
    qrFile: 'carte-visite-harmony-dore',
  },
};

const CartePage = () => {
  const [activeTheme, setActiveTheme] = useState('bordeaux');
  const theme = THEMES[activeTheme];
  const downloadPDF = () => window.open(`/assets/${theme.qrFile}-print.pdf`, '_blank');
  const downloadPNG = () => window.open(`/assets/${theme.qrFile}.png`, '_blank');

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#2E120D] via-[#5C2018] to-[#2E120D] py-16 px-4 flex flex-col items-center justify-center">
      <Link
        to="/"
        className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-[#FDFAF4]/70 hover:text-[#B8922A] mb-8 transition-colors"
        data-testid="carte-back-home"
      >
        <ArrowLeft size={14} /> Retour au site
      </Link>

      {/* Sélecteur de couleur */}
      <div className="flex items-center gap-3 mb-6 bg-[#FDFAF4]/10 backdrop-blur rounded-full p-1.5 border border-[#FDFAF4]/20" data-testid="carte-theme-selector">
        <Palette size={14} className="text-[#FDFAF4]/60 ml-3" />
        {Object.entries(THEMES).map(([slug, t]) => (
          <button
            key={slug}
            type="button"
            onClick={() => setActiveTheme(slug)}
            className={`px-4 py-2 rounded-full text-[11px] uppercase tracking-[0.18em] transition-all flex items-center gap-2 ${
              activeTheme === slug
                ? 'bg-[#FDFAF4] text-[#332724] shadow-md'
                : 'text-[#FDFAF4]/80 hover:text-[#FDFAF4]'
            }`}
            data-testid={`carte-theme-${slug}`}
          >
            <span className="w-3 h-3 rounded-full" style={{ backgroundColor: t.versoTextSwatch }} />
            {t.name}
          </button>
        ))}
      </div>

      {/* Carte recto */}
      <div className="bg-[#FDFAF4] rounded-2xl shadow-[0_30px_80px_rgba(0,0,0,0.4)] p-8 md:p-10 max-w-md w-full mb-6 relative overflow-hidden" data-testid="carte-recto">
        <div className="absolute inset-0 opacity-[0.04]" style={{ backgroundImage: "url('/assets/rose-filigrane.jpg')", backgroundSize: 'cover', backgroundPosition: 'center' }} />
        <div className="relative text-center">
          <div className="w-20 h-20 rounded-full mx-auto mb-5 overflow-hidden border-2 shadow-[0_8px_20px_rgba(184,146,42,0.3)]" style={{ borderColor: theme.versoTextSwatch }}>
            <img src="/assets/logo-harmony.jpg" alt="Logo Harmony" className="w-full h-full object-cover" />
          </div>
          <h1 className={`text-4xl md:text-5xl mb-2 ${theme.rectoTitle}`} style={{ fontFamily: "'Cormorant Garamond', serif", fontStyle: 'italic' }}>
            Maison Harmony
          </h1>
          <div className="flex items-center justify-center gap-3 mb-4">
            <span className="w-12 h-px bg-[#B8922A]" />
            <p className="text-[10px] uppercase tracking-[0.3em] text-[#B8922A]">Parfumerie</p>
            <span className="w-12 h-px bg-[#B8922A]" />
          </div>
          <p className="text-sm text-[#332724] font-light mb-1 italic" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
            Bar à parfums &middot; Concept store
          </p>
          <p className="text-xs text-[#332724]/70 font-light italic">
            « Composez votre signature olfactive »
          </p>
        </div>
      </div>

      {/* Carte verso */}
      <div className={`${theme.versoBg} rounded-2xl shadow-[0_30px_80px_rgba(0,0,0,0.4)] p-8 md:p-10 max-w-md w-full mb-8 relative overflow-hidden`} data-testid="carte-verso">
        <div className="relative grid grid-cols-1 md:grid-cols-[1fr_auto] gap-6 items-center">
          <div className="text-[#FDFAF4]">
            <p className="text-[10px] uppercase tracking-[0.3em] text-[#B8922A] mb-4">Bienvenue chez nous</p>
            <ul className="space-y-3 text-sm font-light">
              <li className="flex items-start gap-2.5">
                <MapPin size={14} className="mt-1 text-[#B8922A] flex-shrink-0" />
                <div>
                  <p>3 Carrefour de l'Hours</p>
                  <p className="text-xs text-[#FDFAF4]/85">Niveau Écluses — Polygone Béziers</p>
                  <p className="text-[10px] text-[#FDFAF4]/65 italic mt-0.5">14 Avenue Sergent Bobillot, 34500 Béziers</p>
                </div>
              </li>
              <li className="flex items-center gap-2.5">
                <Phone size={14} className="text-[#B8922A] flex-shrink-0" />
                <a href="tel:0467215708" className="hover:text-[#B8922A] transition-colors">04 67 21 57 08</a>
              </li>
              <li className="flex items-center gap-2.5">
                <Mail size={14} className="text-[#B8922A] flex-shrink-0" />
                <a href="mailto:contact@maison-harmony.fr" className="hover:text-[#B8922A] transition-colors text-xs">
                  contact@maison-harmony.fr
                </a>
              </li>
              <li className="flex items-center gap-2.5">
                <Globe size={14} className="text-[#B8922A] flex-shrink-0" />
                <a href="https://maison-harmony.fr" target="_blank" rel="noopener noreferrer" className="hover:text-[#B8922A] transition-colors">
                  maison-harmony.fr
                </a>
              </li>
            </ul>
            <div className="border-t border-[#FDFAF4]/15 mt-5 pt-4 text-[10px] text-[#FDFAF4]/60">
              <p className="uppercase tracking-[0.2em] mb-1">Instagram · Facebook · TikTok</p>
              <p className="text-[#B8922A]">@maison.harmony</p>
            </div>
          </div>
          <div className="flex flex-col items-center gap-1 flex-shrink-0 mx-auto md:mx-0">
            <div className="bg-[#FDFAF4] p-2 rounded-lg">
              <img
                src={`https://api.qrserver.com/v1/create-qr-code/?size=180x180&data=https%3A%2F%2Folfactif-harmony.emergent.host&color=${theme.versoTextSwatch.replace('#','')}&bgcolor=FDFAF4&margin=2`}
                alt="QR code Maison Harmony"
                className="w-24 h-24"
                data-testid="carte-qr"
              />
            </div>
            <p className="text-[8px] uppercase tracking-[0.2em] text-[#B8922A] mt-1">Scannez-moi</p>
          </div>
        </div>
      </div>

      <div className="flex flex-wrap gap-3 justify-center max-w-md w-full">
        <button
          onClick={downloadPDF}
          className="flex-1 bg-[#B8922A] hover:bg-[#9a7922] text-[#FDFAF4] px-6 py-3 rounded-full text-xs uppercase tracking-[0.2em] inline-flex items-center justify-center gap-2 transition-colors"
          data-testid="carte-download-pdf"
        >
          <Download size={14} /> PDF {theme.name} print-ready
        </button>
        <button
          onClick={downloadPNG}
          className="flex-1 bg-[#FDFAF4]/10 hover:bg-[#FDFAF4]/20 text-[#FDFAF4] border border-[#FDFAF4]/30 px-6 py-3 rounded-full text-xs uppercase tracking-[0.2em] inline-flex items-center justify-center gap-2 transition-colors"
          data-testid="carte-download-png"
        >
          <Download size={14} /> PNG HD {theme.name}
        </button>
      </div>

      <p className="text-[11px] text-[#FDFAF4]/50 italic mt-6 text-center max-w-md">
        3 versions disponibles · Choisissez votre couleur ci-dessus puis téléchargez le format imprimable pour Vistaprint, Moo, MyBigPrints…
      </p>
    </div>
  );
};

export default CartePage;
